import React, { createContext, useContext, useState, useEffect } from "react";
import { getMe, login as apiLogin, logout as apiLogout, getAppSettings } from "@/lib/api";

const AuthContext = createContext(null);

// Which pages each gestione exposes in its menu
export const GESTIONE_PAGES = {
  azienda: ["azienda_anagrafica"],
  bilancio: ["dashboard"],
  segremat: ["alunni", "alunni_genitori", "scuola_curriculum", "libreria", "scuola_listino", "movimenti", "documenti_catalogo"],
  // 🆕 Feb 2026: gestione virtuale "Tabelle Alunni" — eredita permessi da `segremat`.
  //   Raccoglie le tabelle di dettaglio anagrafico (Idoneità Sportiva, Deleghe Ritiro, Farmaci, ISEE).
  tabelle_alunni: ["moduli_alunni", "deleghe_ritiro", "isee_alunni", "farmaci_annuale", "farmaci_alunni", "diete_alunni", "rid_alunni", "dote_scuola", "tabelle_personalizzate", "documenti_catalogo"],
  // 🆕 Giu 2026: gestione autonoma "Tabelle personalizzate/variabili" — abilitabile da sola in Admin>Utenti.
  tabelle_personalizzate: ["tabelle_personalizzate"],
  // Gestione virtuale "Agevolazioni" — eredita permessi da `segremat` (vedi hasGestione).
  // NON viene mostrata come card autonoma nella Home; si entra dallo shortcut "💰 AGEVOLAZIONI".
  agevolazioni: ["alunni", "alunni_agev", "scuola_agev_report", "comitato_solidale"],
  cedole_prenotazione: ["cedole"],
  libri_prenotazione: ["prenotazioni_libri"],
  comunicator: ["comunicator", "com_modelli", "com_allegati", "com_log"],
  // 🆕 Giu 2026: gestione autonoma "Credito" (gruppo Home "CREDITO", diritto dedicato).
  //   Per ora duplica le pagine di Comunicazioni (voce "Situazioni Contabili").
  credito: ["credito_situazioni", "comunicator", "com_modelli", "com_allegati", "com_log"],
  cassa: ["elaborazione_periodo", "alunni", "movimenti", "tabelle"],
  controlli: ["report_addebiti"],
  prima_nota: ["prima_nota_anagrafica", "prima_nota_fatture", "prima_nota_bilancio", "movimenti", "prima_nota_tabelle"],
  acquisti: ["acquisti_fornitori", "acquisti_ordini", "acquisti_prodotti", "acquisti_email_log"],
  moduli: ["moduli"],
  stati_cassa: ["stati_cassa", "tabelle", "movimenti"],
  // 🆕 Giu 2026: gestione autonoma "Riconciliazione Incassi" (abilitabile in Admin>Utenti).
  riconciliazione: ["riconciliazione_incassi"],
  banca: ["banca", "movimenti"],
  mensa: ["mensa_matrice", "mensa_audit_log", "mensa_storico", "mensa_presenze", "refettori", "mensa_plessi_pomeriggio", "mensa_giorno"],
  mensa_mese: ["mensa_mese_periodi", "mensa_mese_alunni"],
  extra: ["extra_iscrizioni", "extra_matrice", "mensa_audit_log", "extra_stato_corsi", "---", "extra_corsi", "extra_tabella_corsi", "extra_modelli_mail", "extra_log_mail", "extra_impostazioni"],
  richieste_famiglie: ["rf_matrice", "alunni", "mensa_audit_log", "rf_richieste"],
  privacy: ["privacy_azienda", "privacy_nomine", "privacy_trattamenti", "docenti", "privacy_esterni", "privacy_corsi", "privacy_controlli", "---", "privacy_tabelle", "privacy_modelli", "privacy_fascicolo_config", "privacy_fascicolo_dashboard"],
  docenti: ["docenti", "docenti_sicurezza"],
  admin: ["impostazioni", "utenti", "alunni_accreditati"],
  // 🆕 Giu 2026: gestione autonoma "Open Day" (separata da admin, abilitabile in Admin>Utenti).
  openday: ["openday_iscrizioni"],
  analytics: ["analytics_dashboard"],
  portale_log: ["alunni_portal_log"],
  // FAQ: gestione autonoma (NON legata alla mensa). Eredita visibilita' da "mensa"
  // per non togliere l'accesso a chi la usava, oltre al flag dedicato "faq".
  faq: ["faq"],
  // 🆕 Giu 2026: gestione autonoma "Stampe" (log utilizzo stampanti, import CSV nddPrint).
  stampe: ["stampe"],
};

export const GESTIONI_META = [
  { key: "azienda", label: "AZIENDA", short: "AZ", color: "#ffc107", icon: "🏢" },
  { key: "bilancio", label: "RIASSUNTI SCUOLA", short: "BIL", color: "#1565c0", icon: "📊" },
  { key: "segremat", label: "SEGREMATWEB PRO", short: "SGR", color: "#00838f", icon: "🎓" },
  // Gestione virtuale (non mostrata come card autonoma nella Home — vedi HomePortal.jsx).
  { key: "tabelle_alunni", label: "TABELLE FISSE ALUNNI", short: "TAB", color: "#00695c", icon: "🗂" },
  // Gestione autonoma "Tabelle personalizzate/variabili" (abilitabile singolarmente in Admin>Utenti).
  { key: "tabelle_personalizzate", label: "TABELLE PERSONALIZZATE", short: "TVR", color: "#00838f", icon: "🧩" },
  // Gestione virtuale (non mostrata come card autonoma nella Home — vedi HomePortal.jsx).
  { key: "agevolazioni", label: "AGEVOLAZIONI", short: "AGE", color: "#7e57c2", icon: "💰" },
  { key: "cedole_prenotazione", label: "RICHIESTA CEDOLE COMUNI", short: "CDL", color: "#6d4c41", icon: "📚" },
  { key: "libri_prenotazione", label: "PRENOTAZIONE LIBRI", short: "LIB", color: "#4e342e", icon: "📖" },
  { key: "portale_log", label: "LOG PORTALE ALUNNI", short: "LOG", color: "#5d4037", icon: "📋" },
  { key: "comunicator", label: "COMUNICAZIONI", short: "COM", color: "#00acc1", icon: "📧" },
  { key: "cassa", label: "CONTABILITÀ", short: "CON", color: "#2e7d32", icon: "💶" },
  { key: "stati_cassa", label: "STATI DI CASSA", short: "STC", color: "#c62828", icon: "🧾" },
  { key: "riconciliazione", label: "GESTIONE BONIFICI", short: "BON", color: "#ad1457", icon: "💶" },
  { key: "banca", label: "BANCA", short: "BNK", color: "#00695c", icon: "🏦" },
  { key: "prima_nota", label: "PRIMA NOTA", short: "PNT", color: "#00695c", icon: "📒" },
  { key: "acquisti", label: "ACQUISTI", short: "ACQ", color: "#5e35b1", icon: "🛒" },
  { key: "controlli", label: "CONTROLLI", short: "CTR", color: "#5d4037", icon: "🔎" },
  { key: "mensa", label: "MENSA GIORNO", short: "MEN", color: "#8e24aa", icon: "🍽️" },
  { key: "mensa_mese", label: "MENSA MESE", short: "MMS", color: "#ab47bc", icon: "📅" },
  { key: "extra", label: "EXTRA", short: "EXT", color: "#ef6c00", icon: "🎨" },
  { key: "richieste_famiglie", label: "SPOT", short: "RF", color: "#ff9800", icon: "👨‍👩‍👧" },
  { key: "moduli", label: "MODULI", short: "MOD", color: "#ef6c00", icon: "📝" },
  { key: "docenti", label: "DOCENTI", short: "DOC", color: "#0288d1", icon: "👨‍🏫" },
  { key: "portineria", label: "PORTINERIA", short: "PRT", color: "#00897b", icon: "🛎" },
  // 🆕 Giu 2026: permesso dedicato "gestione" di Presenze mensa docenti (listino prezzi, colonna €,
  // importi in stampa, riepilogo annuale). Slegato da "portineria": attivabile per singolo utente.
  { key: "mense_docenti", label: "MENSE DOCENTI (GEST.)", short: "MDG", color: "#00695c", icon: "🍽️" },
  { key: "privacy", label: "PRIVACY", short: "PRV", color: "#37474f", icon: "🔒" },
  { key: "admin", label: "ADMIN", short: "ADM", color: "#6a1b9a", icon: "⚙" },
  { key: "analytics", label: "ANALYTICS", short: "AN", color: "#1a237e", icon: "📊" },
  { key: "faq", label: "FAQ", short: "FAQ", color: "#8e24aa", icon: "❓" },
  { key: "openday", label: "ISCRIZIONI", short: "ISC", color: "#0d3b66", icon: "📝" },
  { key: "stampe", label: "STAMPE", short: "STP", color: "#455a64", icon: "🖨️" },
  { key: "credito", label: "CREDITO", short: "CRD", color: "#0097a7", icon: "💳" },
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [annoCorrente, setAnnoCorrente] = useState(() => {
    const now = new Date();
    return now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  });

  const refreshAnnoCorrente = async () => {
    try {
      const r = await getAppSettings();
      if (r.data?.anno_corrente) setAnnoCorrente(r.data.anno_corrente);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await getMe();
        setUser(res.data);
        await refreshAnnoCorrente();
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (username, password) => {
    const res = await apiLogin(username, password);
    setUser(res.data);
    await refreshAnnoCorrente();
    return res.data;
  };

  const logout = async () => {
    await apiLogout();
    setUser(false);
  };

  const hasGestione = (gestione) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    // Alias gestione virtuale "agevolazioni": eredita i permessi da "segremat".
    if (gestione === "agevolazioni") return !!user.gestioni?.segremat || !!user.gestioni?.agevolazioni;
    // Alias gestione virtuale "tabelle_alunni": eredita i permessi da "segremat".
    if (gestione === "tabelle_alunni") return !!user.gestioni?.segremat || !!user.gestioni?.tabelle_alunni;
    // 🆕 "banca" eredita i permessi da "stati_cassa" (spostata dalla tab a card autonoma feb-2026)
    if (gestione === "banca") return !!user.gestioni?.stati_cassa || !!user.gestioni?.banca;
    // 🆕 "analytics" eredita i permessi da "admin" (spostata dalla tab admin a card autonoma).
    if (gestione === "analytics") return !!user.gestioni?.admin || !!user.gestioni?.analytics;
    // FAQ: gestione autonoma; eredita visibilita' da "mensa" oltre al flag dedicato.
    if (gestione === "faq") return !!user.gestioni?.faq || !!user.gestioni?.mensa;
    return !!user.gestioni?.[gestione];
  };

  const userGestioni = () => {
    if (!user) return [];
    if (user.role === "admin") return GESTIONI_META.map(g => g.key);
    return GESTIONI_META.map(g => g.key).filter(k => !!user.gestioni?.[k]);
  };

  // Check if user can see a page WITHIN the currently-active gestione
  const canSeePageInGestione = (page, gestione) => {
    if (!user) return false;
    if (!gestione) return false;
    if (!hasGestione(gestione)) return false;
    const pages = GESTIONE_PAGES[gestione] || [];
    return pages.includes(page);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasGestione, userGestioni, canSeePageInGestione, annoCorrente, setAnnoCorrente, refreshAnnoCorrente }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
